"use client";

import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import type { LanguageModelUsage } from "ai";
import { useState } from "react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { getTotalTokens } from "@/lib/ai/token-usage";

export type ContextProps = {
  /** Limit of the measured model, only when it matches the selected model. */
  maxTokens?: number;
  /** Final call input + output; a reference value, not the next prompt's size. */
  usedTokens?: number;
  usage: LanguageModelUsage;
  modelId?: string;
  isPrevious?: boolean;
  isPartial?: boolean;
  isModelChanged?: boolean;
};

/** Keeps request consumption separate from the final call's context estimate. */
export function Context({
  maxTokens,
  usedTokens,
  usage,
  modelId,
  isPrevious,
  isPartial,
  isModelChanged,
}: ContextProps) {
  const { i18n } = useLingui();
  const [open, setOpen] = useState(false);
  const hasContext =
    maxTokens !== undefined &&
    Number.isFinite(maxTokens) &&
    maxTokens > 0 &&
    usedTokens !== undefined &&
    Number.isFinite(usedTokens) &&
    usedTokens >= 0;
  const percent = hasContext ? (usedTokens / maxTokens) * 100 : undefined;
  const total = getTotalTokens(usage);
  const hasPartialUsage =
    isPartial ||
    usage.inputTokens === undefined ||
    usage.outputTokens === undefined;
  const exact = (value?: number) =>
    value === undefined ? "—" : i18n.number(value);

  return (
    <HoverCard
      open={open}
      onOpenChange={setOpen}
      closeDelay={100}
      openDelay={100}
    >
      <HoverCardTrigger asChild>
        <button
          className="inline-flex flex-wrap justify-end select-none items-center gap-1 rounded-md px-1.5 py-0 text-left text-xs leading-none text-muted-foreground transition-opacity hover:opacity-90"
          type="button"
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
        >
          {isPrevious ? (
            <span className="whitespace-nowrap">
              <Trans>Previous measurement</Trans> ·{" "}
            </span>
          ) : null}
          {percent !== undefined ? (
            <>
              <span className="whitespace-nowrap">
                <Trans>Context</Trans> ~{Math.round(percent * 10) / 10}%
              </span>
              <ContextIcon percent={percent} />
              <span className="whitespace-nowrap">
                · {formatTokens(usedTokens)} / {formatTokens(maxTokens)}
              </span>
            </>
          ) : (
            <span>
              <Trans>Last request</Trans> · {formatTokens(total)}{" "}
              <Trans>tokens</Trans>
            </span>
          )}
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="end" className="w-80 space-y-3 p-3 text-xs">
        <div>
          <p className="font-medium">
            <Trans>Last request usage</Trans>
          </p>
          {modelId ? (
            <p className="mt-1 break-all text-muted-foreground">{modelId}</p>
          ) : null}
        </div>
        <dl className="space-y-1">
          <div className="flex justify-between gap-4">
            <dt>
              <Trans>Input</Trans>
            </dt>
            <dd>{exact(usage.inputTokens)}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt>
              <Trans>Output</Trans>
            </dt>
            <dd>{exact(usage.outputTokens)}</dd>
          </div>
          {usage.outputTokenDetails.reasoningTokens !== undefined ? (
            <div className="flex justify-between gap-4 text-muted-foreground">
              <dt>
                <Trans>Of output: reasoning</Trans>
              </dt>
              <dd>{exact(usage.outputTokenDetails.reasoningTokens)}</dd>
            </div>
          ) : null}
          <div className="flex justify-between gap-4 font-medium">
            <dt>
              {hasPartialUsage ? (
                <Trans>Reported tokens (partial)</Trans>
              ) : (
                <Trans>Total</Trans>
              )}
            </dt>
            <dd>{exact(total)}</dd>
          </div>
        </dl>
        {hasPartialUsage ? (
          <p className="text-muted-foreground">
            <Trans>
              Some usage details are unavailable. The reported total may be
              incomplete.
            </Trans>
          </p>
        ) : null}
        <p className="text-muted-foreground">
          <Trans>
            Includes all model calls in the last request. This is not your plan
            quota consumption.
          </Trans>
        </p>
        {hasContext ? (
          <div className="space-y-1 border-t pt-2">
            <p className="font-medium">
              <Trans>Context estimate</Trans>
            </p>
            <p>
              {exact(usedTokens)} / {exact(maxTokens)}
            </p>
            <p className="text-muted-foreground">
              <Trans>
                Input and output of the last model call. The next request may
                use a different amount.
              </Trans>
            </p>
          </div>
        ) : (
          <p className="text-muted-foreground">
            {isModelChanged ? (
              <Trans>
                Model changed. Context will be measured after the next response.
              </Trans>
            ) : (
              <Trans>Context estimate unavailable for this response.</Trans>
            )}
          </p>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

/** Clamp only the ring; measured values remain visible even above the limit. */
function ContextIcon({ percent }: { percent: number }) {
  const circumference = 2 * Math.PI * 9;
  return (
    <svg
      aria-hidden="true"
      height={16}
      width={16}
      viewBox="0 0 24 24"
      className="shrink-0 text-amethyst-primary"
    >
      <circle
        cx={12}
        cy={12}
        r={9}
        fill="none"
        opacity={0.25}
        stroke="currentColor"
        strokeWidth={2}
      />
      <circle
        cx={12}
        cy={12}
        r={9}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - Math.min(100, percent) / 100)}
        strokeLinecap="round"
        transform="rotate(-90 12 12)"
      />
    </svg>
  );
}

/** Compact trigger text; the hover card preserves exact integers. */
function formatTokens(tokens?: number) {
  if (tokens === undefined) return "—";
  if (tokens < 1_000) return `${tokens}`;
  if (tokens < 1_000_000) return `${(tokens / 1_000).toFixed(1)}K`;
  if (tokens < 1_000_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  return `${(tokens / 1_000_000_000).toFixed(1)}B`;
}
